// src/actions/files/deleteOutputFiles.ts

import axiosInstance from "../utils/axiosInstance";

/** The backend accepts 1 to 100 keys per request. */
export const MAX_BULK_DELETE_KEYS = 100;

export interface FailedDelete {
  key: string;
  error: string;
}

export interface DeleteOutputFilesResult {
  deleted: string[];
  failed: FailedDelete[];
}

/**
 * Deletes up to 100 output files in one request. A 200 response can still carry a
 * non-empty `failed` list, so callers must check it.
 */
export const deleteOutputFiles = async (
  fileKeys: string[]
): Promise<DeleteOutputFilesResult> => {
  try {
    const response = await axiosInstance.post<DeleteOutputFilesResult>(
      `/utility/delete-output-files`,
      { fileKeys }
    );
    return {
      deleted: response.data?.deleted ?? [],
      failed: response.data?.failed ?? [],
    };
  } catch (error: any) {
    console.error("Error deleting files:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Failed to delete files.");
  }
};

/**
 * Deletes any number of output files by sending them in sequential batches of 100.
 * A batch whose request fails outright is reported as failed for all of its keys,
 * and the remaining batches are still attempted.
 */
export const deleteOutputFilesInBatches = async (
  fileKeys: string[]
): Promise<DeleteOutputFilesResult> => {
  const result: DeleteOutputFilesResult = { deleted: [], failed: [] };

  for (let start = 0; start < fileKeys.length; start += MAX_BULK_DELETE_KEYS) {
    const batch = fileKeys.slice(start, start + MAX_BULK_DELETE_KEYS);
    try {
      const batchResult = await deleteOutputFiles(batch);
      result.deleted.push(...batchResult.deleted);
      result.failed.push(...batchResult.failed);
    } catch (error: any) {
      const message = error?.message || "Failed to delete files.";
      result.failed.push(...batch.map((key) => ({ key, error: message })));
    }
  }

  return result;
};
